import { CameraOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { type ChangeEvent, useRef } from 'react';
import type { ImagemBase64 } from '@/types';
import { fileToBase64 } from '@/std/base64';
import { IMAGE_TYPES } from '@/std/const/image';
import { validateImage } from '@/std/validate';

export function UploadImage({
    src,
    maxSize = 2,
    height = 112,
    width = 112,
    loading = false,
    onChange,
}: {
    src: string | null | undefined;
    maxSize?: number; // em MB
    height?: number;
    width?: number;
    loading?: boolean;
    onChange: (e: ImagemBase64) => void;
}) {
    const inputRef = useRef<HTMLInputElement>(null);

    const onFileChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const file = e.target.files?.[0];

        if (!file) {
            return;
        }

        if (!validateImage(file, maxSize)) {
            return;
        }

        const base64 = await fileToBase64(file);

        onChange({
            imagem_base64: base64.replace(`data:${file.type};base64,`, ''),
            imagem_nome: file.name,
        });
    };

    const uploadFile = (): void => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    const style = { height, width };

    return (
        <div
            className='group relative flex cursor-pointer items-center justify-center bg-gray-200'
            style={style}
        >
            <input
                accept={IMAGE_TYPES}
                className='!hidden'
                onChange={onFileChange}
                ref={inputRef}
                type='file'
            />

            {loading && <Spin className='absolute z-20' size='large' />}

            <div
                className='absolute inset-0 z-10 flex items-center justify-center bg-gray-950 bg-opacity-60 opacity-0 transition-opacity duration-300 group-hover:opacity-100'
                style={style}
            >
                <CameraOutlined className='text-3xl text-white' onClick={uploadFile} />
            </div>

            {src ? (
                <img alt='logo' src={src} style={style} />
            ) : (
                <PlusCircleOutlined className='text-2xl transition-opacity duration-300 group-hover:opacity-0' />
            )}
        </div>
    );
}
